import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Search, Sofa, Briefcase, SprayCan, Package, Building2, Warehouse, Calendar, MoreHorizontal, ArrowRight, Users, FileText, Shield } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import Layout from '../components/layout/Layout';
import ListingCard from '../components/ListingCard';
import EmailModal from '../components/EmailModal';
import { getFeaturedListings, getStats, getCategories } from '../lib/api';
import { getUserEmail } from '../lib/utils';

const categoryIcons = {
    'hotel-furniture': Sofa,
    'hotel-jobs': Briefcase,
    'housekeeping-supplies': SprayCan,
    'guest-supplies': Package,
    'real-estate': Building2,
    'furniture-liquidation': Warehouse,
    'events-announcements': Calendar,
    'misc': MoreHorizontal
};

const HomePage = () => {
    const [searchQuery, setSearchQuery] = useState('');
    const [featuredListings, setFeaturedListings] = useState([]);
    const [categories, setCategories] = useState([]);
    const [stats, setStats] = useState({ total_listings: 0, total_advertisers: 0 });
    const [showEmailModal, setShowEmailModal] = useState(false);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        const fetchData = async () => {
            try {
                const [listingsRes, categoriesRes, statsRes] = await Promise.all([
                    getFeaturedListings(8),
                    getCategories(),
                    getStats()
                ]);
                setFeaturedListings(listingsRes.data);
                setCategories(categoriesRes.data);
                setStats(statsRes.data);
            } catch (error) {
                console.error('Error fetching data:', error);
            } finally {
                setLoading(false);
            }
        };
        fetchData();

        // Show email modal after 3 seconds if no email stored
        const timer = setTimeout(() => {
            if (!getUserEmail()) {
                setShowEmailModal(true);
            }
        }, 3000);

        return () => clearTimeout(timer);
    }, []);

    const handleSearch = (e) => {
        e.preventDefault();
        if (searchQuery.trim()) {
            window.location.href = `/listings?search=${encodeURIComponent(searchQuery)}`;
        }
    };

    return (
        <Layout>
            {/* Hero Section */}
            <section className="hero-gradient text-white py-20 lg:py-28" data-testid="hero-section">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="text-center max-w-3xl mx-auto">
                        <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-bold mb-6 animate-fadeIn">
                            The Premier Marketplace for
                            <span className="text-gold block mt-2">Hotel Industry</span>
                        </h1>
                        <p className="text-lg text-gray-300 mb-8 animate-fadeIn-delay-1">
                            Buy, sell, and discover opportunities in hospitality. From furniture to jobs, real estate to supplies.
                        </p>

                        {/* Search Bar */}
                        <form onSubmit={handleSearch} className="search-bar max-w-2xl mx-auto animate-fadeIn-delay-2" data-testid="search-form">
                            <Search className="w-5 h-5 text-gray-400 ml-4" />
                            <Input
                                type="text"
                                placeholder="Search listings..."
                                value={searchQuery}
                                onChange={(e) => setSearchQuery(e.target.value)}
                                className="border-0 focus-visible:ring-0 focus-visible:ring-offset-0"
                                data-testid="search-input"
                            />
                            <Button type="submit" className="bg-gold hover:bg-amber-600 rounded-none px-8" data-testid="search-btn">
                                Search
                            </Button>
                        </form>

                        {/* Stats */}
                        <div className="flex justify-center gap-12 mt-12 animate-fadeIn-delay-3">
                            <div className="text-center">
                                <div className="text-3xl font-bold text-gold">{stats.total_listings}+</div>
                                <div className="text-sm text-gray-400">Active Listings</div>
                            </div>
                            <div className="text-center">
                                <div className="text-3xl font-bold text-gold">{stats.total_advertisers}+</div>
                                <div className="text-sm text-gray-400">Advertisers</div>
                            </div>
                            <div className="text-center">
                                <div className="text-3xl font-bold text-gold">8</div>
                                <div className="text-sm text-gray-400">Categories</div>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* Categories Section */}
            <section className="py-16 bg-white" data-testid="categories-section">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="text-center mb-12">
                        <h2 className="font-heading text-3xl font-bold text-navy mb-4">Browse by Category</h2>
                        <p className="text-gray-600">Find exactly what you need in our specialized categories</p>
                    </div>

                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4 lg:gap-6">
                        {categories.map((category) => {
                            const IconComponent = categoryIcons[category.slug] || MoreHorizontal;
                            const count = stats.category_counts?.[category.slug] || 0;
                            const bgColor = category.color || 'bg-gray-100 hover:bg-gray-200 border-gray-300';
                            const iconColor = category.icon_color || 'text-navy';
                            return (
                                <Link
                                    key={category.slug}
                                    to={`/listings?category=${category.slug}`}
                                    className={`group rounded-xl p-6 border-2 transition-all duration-300 transform hover:scale-105 hover:shadow-lg ${bgColor}`}
                                    data-testid={`category-${category.slug}`}
                                >
                                    <IconComponent className={`w-10 h-10 ${iconColor} transition-transform duration-300 group-hover:scale-110`} />
                                    <h3 className="font-semibold text-gray-800 mt-4 mb-1">{category.name}</h3>
                                    <p className="text-sm text-gray-600">{count} listings</p>
                                    {category.subcategories?.length > 0 && (
                                        <p className="text-xs text-gray-500 mt-2">
                                            {category.subcategories.length} subcategories
                                        </p>
                                    )}
                                </Link>
                            );
                        })}
                    </div>
                </div>
            </section>

            {/* Featured Listings */}
            <section className="py-16 bg-gray-50" data-testid="featured-section">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="flex justify-between items-center mb-8">
                        <div>
                            <h2 className="font-heading text-3xl font-bold text-navy mb-2">Latest Listings</h2>
                            <p className="text-gray-600">Fresh opportunities in the hotel industry</p>
                        </div>
                        <Link to="/listings">
                            <Button variant="outline" className="hidden sm:flex items-center gap-2">
                                View All <ArrowRight className="w-4 h-4" />
                            </Button>
                        </Link>
                    </div>

                    {loading ? (
                        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
                            {[...Array(4)].map((_, i) => (
                                <div key={i} className="bg-white rounded-lg h-80 animate-pulse" />
                            ))}
                        </div>
                    ) : featuredListings.length > 0 ? (
                        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
                            {featuredListings.map((listing) => (
                                <ListingCard key={listing.id} listing={listing} />
                            ))}
                        </div>
                    ) : (
                        <div className="text-center py-12 bg-white rounded-lg">
                            <FileText className="w-12 h-12 text-gray-300 mx-auto mb-4" />
                            <p className="text-gray-500">No listings yet. Be the first to post!</p>
                            <Link to="/advertiser/register">
                                <Button className="mt-4 bg-gold hover:bg-amber-600">Post an Ad</Button>
                            </Link>
                        </div>
                    )}

                    <div className="text-center mt-8 sm:hidden">
                        <Link to="/listings">
                            <Button variant="outline" className="w-full">
                                View All Listings
                            </Button>
                        </Link>
                    </div>
                </div>
            </section>

            {/* CTA Section */}
            <section className="py-16 bg-navy text-white" data-testid="cta-section">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="grid md:grid-cols-2 gap-12 items-center">
                        <div>
                            <h2 className="font-heading text-3xl lg:text-4xl font-bold mb-4">
                                Start Advertising <span className="text-gold">Free</span> Today
                            </h2>
                            <p className="text-gray-300 mb-6">
                                Join thousands of hotel industry professionals. Create your free account and post unlimited ads - no fees, no catches, completely free forever!
                            </p>
                            <div className="flex flex-col sm:flex-row gap-4">
                                <Link to="/advertiser/register">
                                    <Button className="bg-gold hover:bg-amber-600 text-white w-full sm:w-auto" data-testid="cta-register-btn">
                                        Create Free Account
                                    </Button>
                                </Link>
                                <Link to="/about">
                                    <Button variant="outline" className="border-white text-white hover:bg-white hover:text-navy w-full sm:w-auto">
                                        Learn More
                                    </Button>
                                </Link>
                            </div>
                        </div>
                        <div className="grid grid-cols-2 gap-4">
                            <div className="bg-white/10 rounded-lg p-6">
                                <Users className="w-8 h-8 text-gold mb-3" />
                                <h3 className="font-semibold mb-1">Unlimited Ads</h3>
                                <p className="text-sm text-gray-400">Post as many listings as you need</p>
                            </div>
                            <div className="bg-white/10 rounded-lg p-6">
                                <Shield className="w-8 h-8 text-gold mb-3" />
                                <h3 className="font-semibold mb-1">100% Free</h3>
                                <p className="text-sm text-gray-400">No fees, ever</p>
                            </div>
                            <div className="bg-white/10 rounded-lg p-6">
                                <Building2 className="w-8 h-8 text-gold mb-3" />
                                <h3 className="font-semibold mb-1">Industry Focused</h3>
                                <p className="text-sm text-gray-400">Built for hotel professionals</p>
                            </div>
                            <div className="bg-white/10 rounded-lg p-6">
                                <FileText className="w-8 h-8 text-gold mb-3" />
                                <h3 className="font-semibold mb-1">Easy Management</h3>
                                <p className="text-sm text-gray-400">Full dashboard control</p>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* Email Modal */}
            <EmailModal isOpen={showEmailModal} onClose={() => setShowEmailModal(false)} />
        </Layout>
    );
};

export default HomePage;
